import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class TimezoneService {

  /**
   * Parse UTC offset from timezoneUtcName string
   * Handles formats like: "(UTC+08:00)", "(UTC-05:00)", "(UTC) Coordinated Universal Time"
   * Returns { offsetHours, offsetMinutes } or null if parsing fails
   * Correctly handles negative timezone offsets with minute components (e.g., UTC-03:30)
   */
  parseUtcOffset(timezoneUtcName: string): { offsetHours: number; offsetMinutes: number } | null {
    if (!timezoneUtcName) {
      return null;
    }

    // First, try to match format with explicit offset: "(UTC+08:00)" or "(UTC-03:30)"
    // This regex captures: (1) the sign, (2) the hours, (3) the optional minutes
    const offsetMatch = timezoneUtcName.match(/\(UTC([+-])(\d{2}):?(\d{2})?\)/);

    if (offsetMatch) {
      const sign = offsetMatch[1];
      const hours = parseInt(offsetMatch[2], 10);
      const minutes = parseInt(offsetMatch[3] || '0', 10);

      // Apply the sign to both hours and minutes
      // For UTC-03:30: hours = -3, minutes = -30 (not +30)
      // For UTC+05:45: hours = +5, minutes = +45
      const offsetHours = sign === '+' ? hours : -hours;
      const offsetMinutes = sign === '+' ? minutes : -minutes;

      return { offsetHours, offsetMinutes };
    }

    // Handle plain UTC format: "(UTC) Coordinated Universal Time" or "(UTC)"
    // These represent UTC+00:00 (no offset)
    if (timezoneUtcName.match(/\(UTC\)/)) {
      return { offsetHours: 0, offsetMinutes: 0 };
    }

    return null;
  }

  /**
   * Convert a Date to UTC time that represents the start/end of a day in the user's timezone
   * Independent of browser timezone - works directly with date components
   *
   * @param date - The date to convert (date component is used, time is normalized)
   * @param timezoneUtcName - The timezone name in format "(UTC+HH:MM)"
   * @param setTime - Whether to set a specific time ('start' for 00:00:00 or 'end' for 23:59:59)
   * @returns A new Date where getTime() represents the UTC timestamp for that day boundary in user's timezone
   */
  convertToUserTimezone(date: Date, timezoneUtcName: string, setTime: 'start' | 'end' = 'start'): Date {
    if (!timezoneUtcName) {
      return new Date(date);
    }

    const offset = this.parseUtcOffset(timezoneUtcName);
    if (!offset) {
      return new Date(date);
    }

    // Extract date components from the input date
    // These represent what the user sees in the date picker (in their expected timezone)
    const year = date.getFullYear();
    const month = date.getMonth(); // 0-indexed
    const day = date.getDate();

    // Create a UTC date for this calendar day at 00:00:00 UTC
    // This is our starting reference point
    const utcDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));

    // The user's timezone offset from UTC (in minutes)
    // For UTC+08:00: offsetMinutes = 480
    // For UTC-08:00: offsetMinutes = -480
    const offsetMinutes = (offset.offsetHours * 60) + offset.offsetMinutes;

    // When the user's clock shows this date at 00:00:00, what's the UTC time?
    // Example: User in UTC-08:00 selects July 10
    //   User's 00:00:00 on July 10 = UTC 08:00:00 on July 10
    //   Formula: UTC time = local time - offset
    //   So: UTC = July 10 00:00:00 - (-480 min) = July 10 00:00:00 + 8 hours = July 10 08:00:00 UTC
    //
    // Another example: User in UTC+01:00 selects July 10
    //   User's 00:00:00 on July 10 = UTC 23:00:00 on July 9
    //   Formula: UTC = local time - offset
    //   So: UTC = July 10 00:00:00 - (60 min) = July 10 00:00:00 - 1 hour = July 9 23:00:00 UTC

    const adjustedTime = utcDate.getTime() - (offsetMinutes * 60000);

    if (setTime === 'end') {
      // For end of day: 23:59:59.999
      // Add 23 hours, 59 minutes, 59 seconds, 999 milliseconds to the start of day
      return new Date(adjustedTime + (23 * 60 * 60 * 1000) + (59 * 60 * 1000) + (59 * 1000) + 999);
    }

    return new Date(adjustedTime);
  }

  /**
   * Get the UTC offset label (e.g., "(UTC+08:00)") from timezoneUtcName
   */
  getUtcOffsetLabel(timezoneUtcName: string): string {
    if (!timezoneUtcName) {
      return '(UTC+00:00)';
    }

    const match = timezoneUtcName.match(/\(UTC[+-]\d{2}:\d{2}\)/);
    return match ? match[0] : '(UTC+00:00)';
  }

  /**
   * Get the current date (start of day) in the user's timezone
   * Returns a Date representing "today at 00:00" in the user's timezone
   * @param timezoneUtcName - The timezone name in format "(UTC+HH:MM)" or "(UTC)"
   * @returns A Date object showing today's date in the user's timezone (normalized to 00:00:00)
   */
  getNowInUserTimezone(timezoneUtcName: string): Date {
    const now = new Date();

    if (!timezoneUtcName) {
      // Return today at 00:00:00 in browser's local timezone
      const today = new Date(now);
      today.setHours(0, 0, 0, 0);
      return today;
    }

    const offset = this.parseUtcOffset(timezoneUtcName);
    if (!offset) {
      // Parsing failed, return today at 00:00:00 in browser's local timezone
      const today = new Date(now);
      today.setHours(0, 0, 0, 0);
      return today;
    }

    // Get the current date in the user's timezone by using convertToUserTimezone
    // This extracts the year/month/day as they appear in the user's timezone
    // and returns the start of that day in UTC
    return this.convertToUserTimezone(now, timezoneUtcName, 'start');
  }

  /**
   * Format a UTC date/time as it would appear in the user's timezone
   * Uses UTC component methods to avoid browser timezone interpretation
   * @param utcDate - The UTC date to format
   * @param timezoneUtcName - The timezone name in format "(UTC+HH:MM)" or "(UTC)"
   * @returns Formatted string like "12:30 AM" or "Jul 10, 2026"
   */
  formatDateInTimezone(utcDate: Date, timezoneUtcName: string, format: 'time' | 'date' | 'datetime' = 'datetime'): string {
    const offset = this.parseUtcOffset(timezoneUtcName);
    if (!offset) {
      return utcDate.toLocaleString();
    }

    // Get UTC components
    const utcYear = utcDate.getUTCFullYear();
    const utcMonth = utcDate.getUTCMonth();
    const utcDate_val = utcDate.getUTCDate();
    const utcHours = utcDate.getUTCHours();
    const utcMinutes = utcDate.getUTCMinutes();

    // Debug: Log the input to verify it's correct
    //console.log(`formatDateInTimezone: UTC=${utcYear}-${utcMonth + 1}-${utcDate_val} ${utcHours}:${utcMinutes}, TZ=${timezoneUtcName}, offset=${offset.offsetHours}h ${offset.offsetMinutes}m`);

    // Apply timezone offset
    const offsetMinutes = (offset.offsetHours * 60) + offset.offsetMinutes;

    // Convert to total minutes from start of day, apply offset, then convert back
    let totalMinutes = utcHours * 60 + utcMinutes + offsetMinutes;
    let adjustedYear = utcYear;
    let adjustedMonth = utcMonth;
    let adjustedDate = utcDate_val;

    // Handle day wrapping
    if (totalMinutes < 0) {
      // Went to previous day
      adjustedDate -= 1;
      totalMinutes += 24 * 60; // Add 24 hours worth of minutes

      if (adjustedDate < 1) {
        // Went to previous month
        const prevMonth = new Date(adjustedYear, adjustedMonth, 0);
        adjustedYear = prevMonth.getFullYear();
        adjustedMonth = prevMonth.getMonth();
        adjustedDate = prevMonth.getDate();
      }
    } else if (totalMinutes >= 24 * 60) {
      // Went to next day
      adjustedDate += 1;
      totalMinutes -= 24 * 60; // Subtract 24 hours worth of minutes

      // Check for month/year overflow
      const nextMonth = new Date(adjustedYear, adjustedMonth + 1, 1);
      const daysInMonth = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 0).getDate();

      if (adjustedDate > daysInMonth) {
        adjustedDate = 1;
        adjustedMonth += 1;
        if (adjustedMonth > 11) {
          adjustedMonth = 0;
          adjustedYear += 1;
        }
      }
    }

    // Convert back to hours and minutes
    const adjustedHours = Math.floor(totalMinutes / 60);
    const adjustedMinutes = totalMinutes % 60;

    // Format based on requested format
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Format: "02:45 am" (lowercase am/pm, with leading zero on hour)
    const hour12 = adjustedHours % 12 || 12;
    const ampm = adjustedHours >= 12 ? 'pm' : 'am';
    const timeStr = `${String(hour12).padStart(2, '0')}:${String(adjustedMinutes).padStart(2, '0')} ${ampm}`;

    // Format: "Jul 10 2026 02:45 am" (no commas, single spaces)
    const dateStr = `${monthNames[adjustedMonth]} ${adjustedDate} ${adjustedYear}`;
    const datetimeStr = `${dateStr} ${timeStr}`;

    if (format === 'time') return timeStr;
    if (format === 'date') return dateStr;
    return datetimeStr;
  }

  /**
   * Format a UTC date/time in the user's timezone using the long display
   * format: "Sep 15th, 11:06 AM 2026 (UTC -07:00)".
   * Uses UTC component methods to avoid browser timezone interpretation.
   * @param utcDate - The UTC date to format
   * @param timezoneUtcName - The timezone name in format "(UTC+HH:MM)" or "(UTC)"
   * @returns Formatted string like "Sep 15th, 11:06 AM 2026 (UTC -07:00)"
   */
  formatDateLongInTimezone(utcDate: Date, timezoneUtcName: string): string {
    const offset = this.parseUtcOffset(timezoneUtcName);
    if (!offset) {
      return utcDate.toLocaleString();
    }

    // Get UTC components
    const utcYear = utcDate.getUTCFullYear();
    const utcMonth = utcDate.getUTCMonth();
    const utcDate_val = utcDate.getUTCDate();
    const utcHours = utcDate.getUTCHours();
    const utcMinutes = utcDate.getUTCMinutes();

    // Apply timezone offset
    const offsetMinutes = (offset.offsetHours * 60) + offset.offsetMinutes;

    let totalMinutes = utcHours * 60 + utcMinutes + offsetMinutes;
    let adjustedYear = utcYear;
    let adjustedMonth = utcMonth;
    let adjustedDate = utcDate_val;

    // Handle day wrapping
    if (totalMinutes < 0) {
      adjustedDate -= 1;
      totalMinutes += 24 * 60;

      if (adjustedDate < 1) {
        const prevMonth = new Date(adjustedYear, adjustedMonth, 0);
        adjustedYear = prevMonth.getFullYear();
        adjustedMonth = prevMonth.getMonth();
        adjustedDate = prevMonth.getDate();
      }
    } else if (totalMinutes >= 24 * 60) {
      adjustedDate += 1;
      totalMinutes -= 24 * 60;

      const nextMonth = new Date(adjustedYear, adjustedMonth + 1, 1);
      const daysInMonth = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 0).getDate();

      if (adjustedDate > daysInMonth) {
        adjustedDate = 1;
        adjustedMonth += 1;
        if (adjustedMonth > 11) {
          adjustedMonth = 0;
          adjustedYear += 1;
        }
      }
    }

    const adjustedHours = Math.floor(totalMinutes / 60);
    const adjustedMinutes = totalMinutes % 60;

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Time: "11:06 AM" (uppercase AM/PM)
    const hour12 = adjustedHours % 12 || 12;
    const ampm = adjustedHours >= 12 ? 'PM' : 'AM';
    const timeStr = `${hour12}:${String(adjustedMinutes).padStart(2, '0')} ${ampm}`;

    // Day with ordinal suffix: "15th"
    const dayWithSuffix = `${adjustedDate}${this.getOrdinalSuffix(adjustedDate)}`;

    // Offset label with a space: "(UTC -07:00)"
    const offsetLabel = this.formatOffsetLabel(offset.offsetHours, offset.offsetMinutes);

    return `${monthNames[adjustedMonth]} ${dayWithSuffix}, ${timeStr} ${adjustedYear} ${offsetLabel}`;
  }

  /**
   * Return the ordinal suffix for a day of the month (st, nd, rd, th).
   */
  private getOrdinalSuffix(day: number): string {
    const j = day % 10;
    const k = day % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  }

  /**
   * Build an offset label like "(UTC -07:00)" or "(UTC +05:30)".
   */
  private formatOffsetLabel(offsetHours: number, offsetMinutes: number): string {
    const sign = offsetHours < 0 || offsetMinutes < 0 ? '-' : '+';
    const absHours = Math.abs(offsetHours);
    const absMinutes = Math.abs(offsetMinutes);
    return `(UTC ${sign}${String(absHours).padStart(2, '0')}:${String(absMinutes).padStart(2, '0')})`;
  }
}
